/**
 * Advanced Filters v1 (deals gap-build W2, PM-ruled scope): date range over
 * submitted and/or closing dates, and a deal-value band. The manager ledger
 * adds partner-org and owner selects (option lists are derived from the loaded
 * rows). Filtering is client-side over the already-fetched list and composes
 * (AND) with the existing tab + search behaviour.
 *
 * Status filtering is deliberately NOT in this panel: CHP-364 dropped the green
 * per-status chips in favour of the renamed tabs + the clickable Status-column
 * grouping, so there is no `statuses` group here (CHP-809 removed the dead
 * leftover field + its unmounted `DealStatusChips` control).
 *
 * Date inputs are `yyyy-mm-dd` strings ("" = unset); value bounds are whole
 * dollars as typed ("" = unset). Selects use "" for "any".
 */
export interface DealAdvancedFilterState {
  submittedFrom: string;
  submittedTo: string;
  closingFrom: string;
  closingTo: string;
  valueMin: string;
  valueMax: string;
  /** Manager ledger only — "" on the partner workspace. */
  partnerOrg: string;
  /** Manager ledger only — "" on the partner workspace. */
  owner: string;
}

export const EMPTY_DEAL_FILTERS: DealAdvancedFilterState = {
  submittedFrom: "",
  submittedTo: "",
  closingFrom: "",
  closingTo: "",
  valueMin: "",
  valueMax: "",
  partnerOrg: "",
  owner: "",
};

/**
 * CHP-370: the preset applied when the partner dashboard's "Total Sales Closed
 * YTD" scorecard is clicked — a closing-date range spanning the current
 * calendar year. On the Closed-won tab `closingAt` resolves to `closed_at`
 * (see DealWorkspace), so this scopes the ledger to deals closed this year.
 * `now` is injected so the boundary is deterministic in tests.
 */
export function currentYearClosedFilter(now: Date): DealAdvancedFilterState {
  const year = now.getFullYear();
  return { ...EMPTY_DEAL_FILTERS, closingFrom: `${year}-01-01`, closingTo: `${year}-12-31` };
}

/** The row fields the predicate reads — each host maps its list-item shape
 * onto this so partner + manager grids share one predicate. */
export interface DealFilterFields {
  /** ISO datetime or date string; null = unknown. */
  submittedAt: string | null;
  closingAt: string | null;
  valueCents: number | null;
  partnerOrg?: string | null;
  owner?: string | null;
}

/** Number of active filter groups — drives the "Filters (N)" badge. Each
 * group (submitted range, closing range, value band, org, owner) counts once
 * however many bounds within it are set. */
export function activeDealFilterCount(f: DealAdvancedFilterState): number {
  let n = 0;
  if (f.submittedFrom !== "" || f.submittedTo !== "") n += 1;
  if (f.closingFrom !== "" || f.closingTo !== "") n += 1;
  if (f.valueMin !== "" || f.valueMax !== "") n += 1;
  if (f.partnerOrg !== "") n += 1;
  if (f.owner !== "") n += 1;
  return n;
}

/** Date-only comparison: ISO datetimes and dates both reduce to their first
 * 10 chars (yyyy-mm-dd, UTC) and compare lexicographically. */
function inDateRange(value: string | null, from: string, to: string): boolean {
  if (from === "" && to === "") return true;
  if (value == null) return false; // unknown date can't satisfy an active range
  const day = value.slice(0, 10);
  if (from !== "" && day < from) return false;
  if (to !== "" && day > to) return false;
  return true;
}

function dollarsToCents(input: string): number | null {
  const parsed = Number.parseFloat(input);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
}

export function matchesDealFilters(f: DealAdvancedFilterState, row: DealFilterFields): boolean {
  if (!inDateRange(row.submittedAt, f.submittedFrom, f.submittedTo)) return false;
  if (!inDateRange(row.closingAt, f.closingFrom, f.closingTo)) return false;

  const min = f.valueMin === "" ? null : dollarsToCents(f.valueMin);
  const max = f.valueMax === "" ? null : dollarsToCents(f.valueMax);
  if (min != null || max != null) {
    if (row.valueCents == null) return false; // unknown value can't satisfy a band
    if (min != null && row.valueCents < min) return false;
    if (max != null && row.valueCents > max) return false;
  }

  if (f.partnerOrg !== "" && row.partnerOrg !== f.partnerOrg) return false;
  if (f.owner !== "" && row.owner !== f.owner) return false;

  return true;
}
